import { sortBy } from 'lodash'
import React, { useEffect, useState } from 'react'
import ColumnHeader from '../GridTable/ColumnHeader'
import { FieldValue, TableColumn, TableRecord } from './types'
import {
  Table as ChakraTable,
  Tbody,
  Td,
  Thead,
  Tr,
  Box,
  Center,
  TableProps as ChakraTableProps,
} from '@chakra-ui/react'

export interface TableProps extends ChakraTableProps {
  columns: TableColumn[]
  data: TableRecord[]
  onSort?: (data: TableRecord[]) => void
  defaultSort?: SortState
}

interface SortState {
  field?: string
  ascending: boolean
}

const Table = ({
  columns,
  data,
  onSort,
  defaultSort,
  ...restProps
}: TableProps) => {
  const [sortByRecordField, setSortByRecordField] = useState<SortState>({
    field: defaultSort?.field,
    ascending: defaultSort?.ascending || false,
  })

  const handleSort = (recordField: string, ascending: boolean) =>
    setSortByRecordField({
      field: recordField,
      ascending,
    })

  useEffect(() => {
    if (defaultSort?.field) {
      handleSort(defaultSort.field, defaultSort.ascending)
    }
  }, [])

  const sortedData = (data: TableRecord[]) => {
    const sortedRecords = sortBy(data, record =>
      sortByRecordField.field ? record[sortByRecordField.field] : null
    )
    const _sortedData = sortByRecordField.ascending
      ? sortedRecords
      : sortedRecords.reverse()
    if (onSort) {
      onSort(_sortedData)
    }
    return _sortedData
  }

  const defaultCellRenderer = ({
    value,
    isCentered,
  }: {
    value: FieldValue
    isCentered?: boolean
  }) => {
    if (isCentered) {
      return (
        <Center>
          <Box>{value}</Box>
        </Center>
      )
    }
    return <Box>{value}</Box>
  }

  return (
    <ChakraTable {...restProps}>
      <Thead>
        <Tr>
          {columns.map(
            ({
              key,
              label,
              recordField,
              width,
              isNumeric,
              isCentered = false,
              isSortable = true,
            }: TableColumn) => {
              return (
                <ColumnHeader
                  key={key}
                  label={label}
                  onClick={() =>
                    handleSort(
                      recordField,
                      sortByRecordField.field === recordField
                        ? !sortByRecordField.ascending
                        : false
                    )
                  }
                  isSortingBy={sortByRecordField.field === recordField}
                  sortAscending={sortByRecordField.ascending}
                  width={width}
                  isNumeric={isNumeric}
                  isCentered={isCentered}
                  isSortable={isSortable}
                />
              )
            }
          )}
        </Tr>
      </Thead>
      <Tbody>
        {sortedData(data).map((record: TableRecord) => {
          return (
            <Tr key={record.key}>
              {columns.map(
                ({
                  key,
                  recordField,
                  isNumeric,
                  isCentered,
                  cellRenderer,
                }: TableColumn) => {
                  const value = record[recordField]
                  const Cell = cellRenderer || defaultCellRenderer
                  return (
                    <Td key={key} isNumeric={!!isNumeric}>
                      {isCentered ? (
                        <Center>
                          <Cell value={value} />
                        </Center>
                      ) : (
                        <Cell value={value} />
                      )}
                    </Td>
                  )
                }
              )}
            </Tr>
          )
        })}
      </Tbody>
    </ChakraTable>
  )
}

export default Table
